import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { IconEye, IconEyeOff, IconHelpCircle } from '@tabler/icons-react';
import { useAuth } from '../context/AuthContext';
import { getPostLoginPath } from '../utils/navigation';
import { safeInternalPath } from '../utils/safeRedirect';
import {
    getLoginFormatError,
    isValidLoginFormat,
    LOGIN_EXAMPLE,
    LOGIN_HINT_TEXT,
    LOGIN_MAX_LENGTH,
    normalizeLogin,
    sanitizeLoginInput,
} from '../utils/login';
import {
    getConfirmPasswordInlineError,
    getPasswordFormatError,
    getPasswordInlineError,
    isValidPasswordFormat,
    PASSWORD_MAX_LENGTH,
    sanitizePasswordInput,
} from '../utils/password';
import FormField from './ui/FormField';
import Checkbox from './ui/Checkbox';
import ErrorMessage from './ui/ErrorMessage';
import Loader from './ui/Loader';
import LogoBrand from './ui/LogoBrand';
import { track, trackErrorCode } from '../utils/analytics';
import './LoginPage.scss';

function PasswordToggle({ showPassword, onToggle }) {
    return (
        <button
            type="button"
            className="login-page__toggle"
            onClick={onToggle}
            aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
        >
            {showPassword ? (
                <IconEyeOff size={18} stroke={1.5} />
            ) : (
                <IconEye size={18} stroke={1.5} />
            )}
        </button>
    );
}

function LoginHintToggle({ open, onToggle, buttonRef }) {
    return (
        <button
            ref={buttonRef}
            type="button"
            className={`login-page__toggle${open ? ' login-page__toggle--active' : ''}`}
            onClick={onToggle}
            aria-label="Подсказка по логину"
            aria-expanded={open}
        >
            <IconHelpCircle size={18} stroke={1.5} />
        </button>
    );
}

export default function LoginPage() {
    const [mode, setMode] = useState('login');
    const [login, setLogin] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
    const [agreedToRules, setAgreedToRules] = useState(false);
    const [loginHintOpen, setLoginHintOpen] = useState(false);
    const loginHintButtonRef = useRef(null);
    const loginHintPopoverRef = useRef(null);
    const { login: authLogin, register: authRegister, isAuthenticated, isLoading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const isRegister = mode === 'register';

    const resolveRedirect = useCallback(async () => {
        const explicit = searchParams.get('redirect');
        if (explicit) return safeInternalPath(explicit);
        return getPostLoginPath();
    }, [searchParams]);

    useEffect(() => {
        if (!isAuthenticated) return;
        resolveRedirect().then((path) => navigate(path, { replace: true }));
    }, [isAuthenticated, navigate, resolveRedirect]);

    const resetFormState = () => {
        setError('');
        setFieldErrors({});
        setPassword('');
        setConfirmPassword('');
        setShowPassword(false);
        setShowConfirmPassword(false);
        setAgreedToPrivacy(false);
        setAgreedToRules(false);
    };

    const switchMode = (nextMode) => {
        if (nextMode === mode) return;
        setMode(nextMode);
        setLoginHintOpen(false);
        resetFormState();
    };

    const closeLoginHint = useCallback(() => setLoginHintOpen(false), []);

    useEffect(() => {
        if (!loginHintOpen) return undefined;

        const handlePointerDown = (event) => {
            const target = event.target;
            if (loginHintButtonRef.current?.contains(target)) return;
            if (loginHintPopoverRef.current?.contains(target)) return;
            closeLoginHint();
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') closeLoginHint();
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [loginHintOpen, closeLoginHint]);

    const completeAuth = async (authFn, loginValue, passwordValue) => {
        setLoading(true);
        setError('');
        try {
            await authFn(loginValue, passwordValue);
            track(isRegister ? 'auth_register_success' : 'auth_login_success');
            const path = await resolveRedirect();
            navigate(path, { replace: true });
        } catch (err) {
            trackErrorCode(
                isRegister ? 'auth_register_error' : 'auth_login_error',
                err.message || (isRegister ? 'Ошибка регистрации' : 'Ошибка входа'),
            );
            setError(err.message || (isRegister ? 'Ошибка регистрации' : 'Ошибка входа'));
        } finally {
            setLoading(false);
        }
    };

    const validateForm = () => {
        const nextErrors = {};
        const loginError = getLoginFormatError(login);
        if (loginError) nextErrors.login = loginError;
        const passwordError = getPasswordFormatError(password, {
            strict: isRegister,
            login: isRegister ? login : undefined,
        });
        if (passwordError) nextErrors.password = passwordError;
        if (isRegister) {
            if (!confirmPassword) {
                nextErrors.confirmPassword = 'Повторите пароль';
            } else if (password !== confirmPassword) {
                nextErrors.confirmPassword = 'Пароли не совпадают';
            }
            if (!agreedToPrivacy) {
                nextErrors.agreedToPrivacy = 'Необходимо согласие на обработку персональных данных';
            }
            if (!agreedToRules) {
                nextErrors.agreedToRules = 'Необходимо принять правила использования';
            }
        }
        setFieldErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const loginValue = normalizeLogin(login);

        if (isRegister) {
            await completeAuth(authRegister, loginValue, password);
        } else {
            await completeAuth(authLogin, loginValue, password);
        }
    };

    const isValid = isRegister
        ? isValidLoginFormat(login)
            && isValidPasswordFormat(password, { strict: true, login })
            && password === confirmPassword
            && confirmPassword
            && agreedToPrivacy
            && agreedToRules
        : isValidLoginFormat(login) && isValidPasswordFormat(password);

    if (authLoading || isAuthenticated) {
        return <Loader text="Проверяем сессию..." />;
    }

    return (
        <div className="login-page">
            <div className="login-page__hero">
                <LogoBrand variant="hero" as="h1" className="login-page__brand" />
                <p className="login-page__tagline">
                    Личный кабинет — теперь в браузере
                </p>
            </div>

            <div className="login-page__form-wrap">
                <div className="card login-page__card">
                    <div className="login-page__tabs" role="tablist" aria-label="Вход или регистрация">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={!isRegister}
                            className={`login-page__tab${!isRegister ? ' login-page__tab--active' : ''}`}
                            onClick={() => switchMode('login')}
                        >
                            Вход
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={isRegister}
                            className={`login-page__tab${isRegister ? ' login-page__tab--active' : ''}`}
                            onClick={() => switchMode('register')}
                        >
                            Регистрация
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} noValidate>
                        <ErrorMessage message={error} />

                        <div className="login-page__login-field">
                            <FormField
                                label="Логин"
                                value={login}
                                onChange={(e) => {
                                    const nextLogin = sanitizeLoginInput(e.target.value);
                                    setLogin(nextLogin);
                                    setFieldErrors((prev) => {
                                        const next = { ...prev, login: '' };
                                        if (isRegister && password) {
                                            next.password = getPasswordInlineError(password, { login: nextLogin });
                                        }
                                        return next;
                                    });
                                }}
                                placeholder="username"
                                autoComplete="username"
                                maxLength={LOGIN_MAX_LENGTH}
                                inputMode="text"
                                autoCapitalize="off"
                                spellCheck={false}
                                error={fieldErrors.login}
                                rightElement={(
                                    <LoginHintToggle
                                        open={loginHintOpen}
                                        onToggle={() => setLoginHintOpen((prev) => !prev)}
                                        buttonRef={loginHintButtonRef}
                                    />
                                )}
                            />
                            {loginHintOpen && (
                                <div
                                    ref={loginHintPopoverRef}
                                    className="login-page__login-hint-popover"
                                    role="tooltip"
                                    id="login-hint-popover"
                                >
                                    <p className="login-page__login-hint-text">{LOGIN_HINT_TEXT}</p>
                                    <p className="login-page__login-hint-example">
                                        Например:
                                        {' '}
                                        <span>{LOGIN_EXAMPLE}</span>
                                    </p>
                                </div>
                            )}
                        </div>

                        <FormField
                            label="Пароль"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => {
                                const nextPassword = sanitizePasswordInput(e.target.value);
                                setPassword(nextPassword);
                                setFieldErrors((prev) => ({
                                    ...prev,
                                    password: isRegister
                                        ? getPasswordInlineError(nextPassword, { login })
                                        : '',
                                    ...(isRegister && confirmPassword
                                        ? {
                                            confirmPassword: getConfirmPasswordInlineError(
                                                nextPassword,
                                                confirmPassword,
                                            ),
                                        }
                                        : {}),
                                }));
                            }}
                            placeholder="••••••••"
                            autoComplete={isRegister ? 'new-password' : 'current-password'}
                            maxLength={PASSWORD_MAX_LENGTH}
                            error={fieldErrors.password}
                            rightElement={
                                <PasswordToggle
                                    showPassword={showPassword}
                                    onToggle={() => setShowPassword(!showPassword)}
                                />
                            }
                        />

                        {isRegister && (
                            <FormField
                                label="Повторите пароль"
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => {
                                    const nextConfirm = sanitizePasswordInput(e.target.value);
                                    setConfirmPassword(nextConfirm);
                                    setFieldErrors((prev) => ({
                                        ...prev,
                                        confirmPassword: getConfirmPasswordInlineError(
                                            password,
                                            nextConfirm,
                                        ),
                                    }));
                                }}
                                placeholder="••••••••"
                                autoComplete="new-password"
                                maxLength={PASSWORD_MAX_LENGTH}
                                error={fieldErrors.confirmPassword}
                                rightElement={
                                    <PasswordToggle
                                        showPassword={showConfirmPassword}
                                        onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
                                    />
                                }
                            />
                        )}

                        {isRegister && (
                            <div className="login-page__consent">
                                <div className="login-page__consent-item">
                                    <Checkbox
                                        id="register-privacy-consent"
                                        className="login-page__consent-checkbox"
                                        checked={agreedToPrivacy}
                                        onChange={(e) => {
                                            setAgreedToPrivacy(e.target.checked);
                                            setFieldErrors((prev) => ({ ...prev, agreedToPrivacy: '' }));
                                        }}
                                        label={(
                                            <>
                                                Я даю согласие на обработку моих персональных данных на условиях
                                                {' '}
                                                <Link
                                                    to="/privacy"
                                                    className="login-page__consent-link"
                                                    onMouseDown={(e) => e.preventDefault()}
                                                >
                                                    Политики конфиденциальности
                                                </Link>
                                            </>
                                        )}
                                    />
                                    {fieldErrors.agreedToPrivacy && (
                                        <p className="login-page__consent-error" role="alert">
                                            {fieldErrors.agreedToPrivacy}
                                        </p>
                                    )}
                                </div>

                                <div className="login-page__consent-item">
                                    <Checkbox
                                        id="register-rules-consent"
                                        className="login-page__consent-checkbox"
                                        checked={agreedToRules}
                                        onChange={(e) => {
                                            setAgreedToRules(e.target.checked);
                                            setFieldErrors((prev) => ({ ...prev, agreedToRules: '' }));
                                        }}
                                        label={(
                                            <>
                                                Я подтверждаю, что прочитал(а) и принимаю
                                                {' '}
                                                <Link
                                                    to="/terms"
                                                    className="login-page__consent-link"
                                                    onMouseDown={(e) => e.preventDefault()}
                                                >
                                                    Правила использования сайта
                                                </Link>
                                            </>
                                        )}
                                    />
                                    {fieldErrors.agreedToRules && (
                                        <p className="login-page__consent-error" role="alert">
                                            {fieldErrors.agreedToRules}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="login-page__actions">
                            <button
                                type="submit"
                                className="btn btn--primary btn--block"
                                disabled={!isValid || loading}
                            >
                                {loading
                                    ? (isRegister ? 'Создаём аккаунт...' : 'Входим...')
                                    : (isRegister ? 'Зарегистрироваться' : 'Войти')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
